"use client";
import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { RefreshCw, ExternalLink, X, Plus } from "lucide-react";
import { useAuthedApi } from "../../api/useAuthedApi";
import { RestError } from "../../api/restClient";
import type {
  AddMcpInput,
  DiscoveredTool,
  McpAuthMode,
  McpScope,
  McpTransport,
  StaticSecretMode,
} from "../../api/customMcpApi";

/** Four-step wizard: (1) connection + auth, (2) discover tools, (3) curate,
 *  (4) done. Rendered as a MUI Dialog to match ComponentSelectionDialog.
 *  The pasted secret is write-only — a password field, never read back. */
/** Prefill for editing an existing connector. The secret is never returned by
 *  the backend (write-only), so editing always requires re-entering it — the
 *  re-add upserts (revokes the prior secret, stores the new one). */
export interface McpEditInitial {
  name: string;
  display_name: string;
  mcp_url: string;
  auth_mode: McpAuthMode;
  /** Non-secret static-auth shape (from the listing) used to restore the exact
   *  UI mode — e.g. a static/header secret on the X-Client-Credentials header
   *  is really the client-credentials mode, not a plain header token. */
  static_mode?: StaticSecretMode | null;
  static_header_name?: string | null;
}

interface Props {
  open: boolean;
  scope: McpScope;
  onClose: () => void;
  /** Called after a successful add (+ optional curate) so the list refreshes. */
  onAdded: () => void;
  /** When set, the wizard opens in edit mode prefilled from this connector. */
  editInitial?: McpEditInitial | null;
}

type Step = "connect" | "discover" | "curate" | "done";

/** Wizard-only auth modes. "client_credentials" is sugar over static/header;
 *  "custom_headers" collects an arbitrary list of request headers (each marked
 *  sensitive or not) → the backend custom_headers {plain, encrypted} maps. */
type UiAuthMode =
  | McpAuthMode
  | "client_credentials"
  | "custom_headers"
  // Per-user static: each end-user pastes THEIR own token via
  // POST /plugins/v1/connectors/{slug}/secret with their own userJWT. The
  // company-wide install carries only wiring (mode + header_name) — no shared
  // secret row. Only meaningful on scope=company installs; scope=user is
  // per-user by definition already.
  | "per_user_static";

/** One editable request header in the custom_headers editor. */
interface HeaderRow {
  name: string;
  value: string;
  /** Sensitive values are encrypted at rest (the `encrypted` map); non-sensitive
   *  go in `plain` (stored/readable as-is, e.g. a base URL). */
  sensitive: boolean;
}

/** Generic header that carries an OAuth2 client-credentials bundle to any MCP
 *  that does its own token exchange. Not connector-specific. */
const CLIENT_CREDS_HEADER = "X-Client-Credentials";

export function AddMcpWizard({ open, scope, onClose, onAdded, editInitial }: Props) {
  const { customMcp } = useAuthedApi();
  const isEdit = !!editInitial;

  const [step, setStep] = useState<Step>("connect");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Step 1 — connection + auth
  const [name, setName] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [description, setDescription] = useState("");
  const [url, setUrl] = useState("");
  const [transport, setTransport] = useState<McpTransport>("streamable_http");
  // UI auth mode. "client_credentials" is a UX convenience that compiles down
  // to a static/header secret carrying a {client_id, client_secret} JSON
  // bundle in the generic X-Client-Credentials header — the connector-agnostic
  // shape any client-credentials MCP (Toast, etc.) reads + exchanges itself.
  const [authMode, setAuthMode] = useState<UiAuthMode>("static");
  const [secretMode, setSecretMode] = useState<StaticSecretMode>("bearer");
  const [headerName, setHeaderName] = useState("Authorization");
  const [secret, setSecret] = useState("");
  // OAuth fields
  const [authUrl, setAuthUrl] = useState("");
  const [tokenUrl, setTokenUrl] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [scopes, setScopes] = useState("");
  // Custom-headers mode: an arbitrary list of request headers. Each row has a
  // name + value + a `sensitive` flag (sensitive → encrypted at rest). Compiles
  // to the backend custom_headers {plain, encrypted} maps.
  const [headerRows, setHeaderRows] = useState<HeaderRow[]>([
    { name: "", value: "", sensitive: false },
  ]);

  // Step 2/3 — discovery + curation
  const [tools, setTools] = useState<DiscoveredTool[]>([]);
  const [needsAuth, setNeedsAuth] = useState(false);
  const [authorizeUrl, setAuthorizeUrl] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const reset = () => {
    setStep("connect");
    setBusy(false);
    setError(null);
    setName("");
    setDisplayName("");
    setDescription("");
    setUrl("");
    setTransport("streamable_http");
    setAuthMode("static");
    setSecretMode("bearer");
    setHeaderName("Authorization");
    setSecret("");
    setAuthUrl("");
    setTokenUrl("");
    setClientId("");
    setClientSecret("");
    setScopes("");
    setHeaderRows([{ name: "", value: "", sensitive: false }]);
    setTools([]);
    setNeedsAuth(false);
    setAuthorizeUrl(null);
    setSelected(new Set());
  };

  const close = () => {
    reset();
    onClose();
  };

  // Prefill when opening in edit mode. Name is fixed (identity/upsert key);
  // secret is intentionally left blank — it's write-only and must be re-entered.
  useEffect(() => {
    if (open && editInitial) {
      setName(editInitial.name);
      setDisplayName(editInitial.display_name);
      setUrl(editInitial.mcp_url);
      // Map stored auth_mode back to a UI mode. A static/header secret on the
      // generic X-Client-Credentials header is really the client-credentials
      // mode — restore that so the Client ID / secret fields reappear. Any
      // other static header restores as a custom-header token.
      if (
        editInitial.auth_mode === "static" &&
        editInitial.static_mode === "header" &&
        editInitial.static_header_name === CLIENT_CREDS_HEADER
      ) {
        setAuthMode("client_credentials");
      } else {
        setAuthMode(editInitial.auth_mode);
        if (editInitial.static_mode) setSecretMode(editInitial.static_mode);
        if (editInitial.static_header_name) setHeaderName(editInitial.static_header_name);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editInitial]);

  // Identity is server-generated (opaque `c_<uuid>`) — the user never types a
  // slug. On EDIT, `name` is the existing immutable identity (carried via
  // editInitial); on ADD it stays empty and the backend mints one.
  const canSubmitStep1 =
    displayName.trim().length > 0 &&
    /^https?:\/\//i.test(url) &&
    (authMode === "static"
      ? secret.trim().length > 0
      : authMode === "per_user_static"
        ? // Per-user: no secret at install time. Header mode still needs a
          // header_name so the runtime knows which header to set per-user.
          secretMode !== "header" || headerName.trim().length > 0
        : authMode === "client_credentials"
          ? clientId.trim().length > 0 && clientSecret.trim().length > 0
          : authMode === "custom_headers"
            ? headerRows.some((r) => r.name.trim().length > 0 && r.value.length > 0)
            : authMode === "mcp_oauth" || authMode === "none"
              ? true // none: no credential. mcp_oauth: URL only (negotiated later).
              : authUrl.trim().length > 0 &&
                tokenUrl.trim().length > 0 &&
                clientId.trim().length > 0);

  const buildInput = (): AddMcpInput => {
    // MCP-native OAuth: paste-a-URL only. No secret/oauth block — the provider
    // is discovered + dynamically registered lazily on first connect.
    if (authMode === "mcp_oauth") {
      return {
        ...(name ? { name } : {}),
        display_name: displayName,
        description: description || undefined,
        mcp_url: url,
        transport,
        auth_mode: "mcp_oauth",
      };
    }
    // "client_credentials" is sugar: store a static/header secret carrying the
    // {client_id, client_secret} JSON in the generic X-Client-Credentials
    // header. The MCP server does its own client-credentials token exchange.
    if (authMode === "client_credentials") {
      return {
        ...(name ? { name } : {}),
        display_name: displayName,
        description: description || undefined,
        mcp_url: url,
        transport,
        auth_mode: "static",
        static_auth: {
          mode: "header",
          header_name: CLIENT_CREDS_HEADER,
          secret: JSON.stringify({
            client_id: clientId.trim(),
            client_secret: clientSecret.trim(),
          }),
        },
      };
    }
    // "per_user_static" installs the connector with static-auth WIRING (mode +
    // header_name) but no shared secret. Each end-user attaches their own
    // token via POST /plugins/v1/connectors/{slug}/secret. Emitted with
    // per_user=true + secret omitted; the backend refuses per_user=true when
    // secret is present, so the two flags cannot be set together.
    if (authMode === "per_user_static") {
      return {
        ...(name ? { name } : {}),
        display_name: displayName,
        description: description || undefined,
        mcp_url: url,
        transport,
        auth_mode: "static",
        static_auth: {
          mode: secretMode,
          header_name: secretMode === "header" ? headerName : undefined,
          per_user: true,
        },
      };
    }
    // "custom_headers" collects an arbitrary list of request headers. Rows split
    // into the backend's `plain` (non-sensitive) and `encrypted` (sensitive)
    // maps; the connector emits their union per request.
    if (authMode === "custom_headers") {
      const plain: Record<string, string> = {};
      const encrypted: Record<string, string> = {};
      for (const r of headerRows) {
        const nm = r.name.trim();
        if (!nm) continue;
        (r.sensitive ? encrypted : plain)[nm] = r.value;
      }
      return {
        ...(name ? { name } : {}),
        display_name: displayName,
        description: description || undefined,
        mcp_url: url,
        transport,
        auth_mode: "static",
        static_auth: { mode: "custom_headers", plain, encrypted },
      };
    }
    return {
      ...(name ? { name } : {}),
      display_name: displayName,
      description: description || undefined,
      mcp_url: url,
      transport,
      auth_mode: authMode,
      static_auth:
        authMode === "static"
          ? {
              mode: secretMode,
              header_name: secretMode === "header" ? headerName : undefined,
              secret,
            }
          : undefined,
      oauth:
        authMode === "user_oauth"
          ? {
              auth_url: authUrl,
              token_url: tokenUrl,
              client_id: clientId,
              client_secret: clientSecret,
              scopes: scopes
                .split(/[\s,]+/)
                .map((s) => s.trim())
                .filter(Boolean),
            }
          : undefined,
    };
  };

  /** Step 1 → add the connector, then run discovery. */
  const handleAddAndDiscover = async () => {
    setBusy(true);
    setError(null);
    try {
      // The identity is server-generated (opaque c_<uuid>) — capture the
      // returned name and use it for the rest of the wizard. Without this,
      // discover/connect/saveSkills fell back to the (now-empty) local `name`
      // state and hit /plugins/v1/mcp//discover → 404.
      const added = await customMcp.add(scope, buildInput());
      if (added?.name) setName(added.name);
      setSecret(""); // drop the pasted secret from memory once sent
      setClientSecret("");
      await runDiscover(added?.name);
    } catch (e) {
      console.error("[AddMcpWizard] add/discover failed", e);
      setError(e instanceof Error ? e.message : "Failed to add connector");
      setBusy(false);
    }
  };

  /** Open the OAuth consent window. Both OAuth modes build the authorize URL via
   *  POST /authorize (server-side: mcp_oauth negotiates the provider first, then
   *  mints PKCE + state; user_oauth just mints them). The discover probe does
   *  NOT return authorize_url for either — so always call connect() rather than
   *  relying on a pre-fetched URL. (If a prior discover DID surface one — legacy
   *  path — honor it as a fallback.) */
  const handleConnect = async () => {
    setBusy(true);
    setError(null);
    try {
      const { authorize_url } = await customMcp.connect(name);
      window.open(authorize_url, "_blank", "noopener");
    } catch (e) {
      console.error("[AddMcpWizard] oauth connect failed", e);
      // Prefer the structured backend hint (RestError.fallback) over a regex on
      // the message. `manual_oauth` = the server has no OAuth discovery, so Auto
      // can't work — tell the user to use a token instead.
      const fallback = e instanceof RestError ? e.fallback : undefined;
      if (fallback === "manual_oauth") {
        setError(
          "This server doesn't support automatic sign-in. Re-add it with a static token (Shared credential / Custom headers) or configure OAuth manually.",
        );
      } else if (authorizeUrl) {
        // Legacy fallback: a discover response that already carried the URL.
        window.open(authorizeUrl, "_blank", "noopener");
      } else {
        setError(e instanceof Error ? e.message : "Connect failed");
      }
    } finally {
      setBusy(false);
    }
  };

  /** Static per-user: attach the caller's token, then retry discovery so the
   *  server probes the MCP with the freshly-stored credential and populates
   *  the curation list. Uses the current secretMode/headerName the user
   *  picked in step 1 (kept in state, same values the install manifest set).
   */
  const handleAttachSecret = async () => {
    setBusy(true);
    setError(null);
    try {
      await customMcp.attachSecret(name, {
        mode: secretMode === "custom_headers" ? "bearer" : secretMode,
        header_name: secretMode === "header" ? headerName : undefined,
        secret: secret.trim(),
      });
      setSecret(""); // drop the pasted secret from memory once sent
      await runDiscover(name);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save token");
    } finally {
      setBusy(false);
    }
  };

  const runDiscover = async (nameOverride?: string) => {
    // setName() from the add step hasn't flushed to `name` yet this render;
    // use the freshly-returned identity when provided. Guard against a
    // non-string override (e.g. a click handler passing the event object,
    // which would URL-encode to `[object Object]` and 404 the discover call).
    const effName =
      typeof nameOverride === "string" && nameOverride ? nameOverride : name;
    setBusy(true);
    setError(null);
    try {
      const res = await customMcp.discover(scope, effName);
      setNeedsAuth(Boolean(res.needs_auth));
      setAuthorizeUrl(res.authorize_url ?? null);
      setTools(res.tools ?? []);
      // Pre-select everything so curation is opt-out.
      setSelected(new Set((res.tools ?? []).map((t) => t.upstream_name)));
      if (!res.server_up && !res.needs_auth) {
        setError("Couldn't reach the MCP server. It was added — retry discovery.");
      }
      setStep("discover");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Discovery failed");
    } finally {
      setBusy(false);
    }
  };

  const toggle = (tool: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(tool)) next.delete(tool);
      else next.add(tool);
      return next;
    });
  };

  /** Step 3 → persist the curated allowlist. */
  const handleSaveSkills = async () => {
    setBusy(true);
    setError(null);
    try {
      await customMcp.saveSkills(scope, name, Array.from(selected));
      setStep("done");
      onAdded();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save skills");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEdit ? "Edit MCP server" : "Add MCP server"}
        {scope === "user" ? " (personal)" : " (company)"}
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity={needsAuth ? "info" : "error"} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {step === "connect" && (
          <Stack spacing={2} sx={{ mt: 1 }}>
            {/* No slug field — the identity is server-generated (opaque
                c_<uuid>). Editing shows it read-only for reference; adding
                collects only a human display name. */}
            {isEdit && (
              <TextField
                label="Connector id"
                size="small"
                value={name}
                disabled
                helperText="System-generated id (immutable). Re-enter the secret below to update."
                slotProps={{ htmlInput: { "aria-label": "mcp-name" } }}
              />
            )}
            <TextField
              label="Display name"
              size="small"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              helperText="Shown in the plugins list. Pick anything readable."
            />
            <TextField
              label="Description"
              size="small"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              multiline
              minRows={2}
            />
            <TextField
              label="MCP server URL"
              size="small"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://mcp.example.com/mcp"
            />
            <TextField
              select
              label="Transport"
              size="small"
              value={transport}
              onChange={(e) => setTransport(e.target.value as McpTransport)}
            >
              <MenuItem value="streamable_http">Streamable HTTP</MenuItem>
              <MenuItem value="sse">SSE</MenuItem>
            </TextField>

            <Divider textAlign="left">
              <Typography variant="caption" color="text.secondary">
                Authentication
              </Typography>
            </Divider>

            <TextField
              select
              label="Auth mode"
              size="small"
              value={authMode}
              onChange={(e) => setAuthMode(e.target.value as UiAuthMode)}
            >
              <MenuItem value="none">
                None — public server, no credential (e.g. Microsoft Learn)
              </MenuItem>
              <MenuItem value="mcp_oauth">
                Auto — sign in via server (URL only)
              </MenuItem>
              <MenuItem value="static">
                {scope === "company"
                  ? "Shared credential — everyone uses one login (paste a token)"
                  : "Static secret (paste a token)"}
              </MenuItem>
              {scope === "company" && (
                <MenuItem value="per_user_static">
                  Each user pastes their own token (per-user)
                </MenuItem>
              )}
              <MenuItem value="client_credentials">
                Client credentials (client ID + secret)
              </MenuItem>
              <MenuItem value="custom_headers">
                Custom headers (add your own request headers)
              </MenuItem>
              <MenuItem value="user_oauth">
                {scope === "company"
                  ? "Each user signs in with their own account (OAuth)"
                  : "OAuth (sign in with your account)"}
              </MenuItem>
            </TextField>

            {authMode === "none" && (
              <Typography variant="caption" color="text.secondary">
                This server is public — no token, no sign-in. Its tools load
                straight away once added.
              </Typography>
            )}
            {authMode === "mcp_oauth" && (
              <Typography variant="caption" color="text.secondary">
                No credentials needed here. This server handles its own sign-in —
                you&apos;ll be prompted to connect the first time it&apos;s used.
              </Typography>
            )}

            {authMode === "static" && (
              <>
                <TextField
                  select
                  label="Secret type"
                  size="small"
                  value={secretMode}
                  onChange={(e) => setSecretMode(e.target.value as StaticSecretMode)}
                >
                  <MenuItem value="bearer">Bearer token</MenuItem>
                  <MenuItem value="header">Custom header</MenuItem>
                  <MenuItem value="basic">Basic auth</MenuItem>
                </TextField>
                {secretMode === "header" && (
                  <TextField
                    label="Header name"
                    size="small"
                    value={headerName}
                    onChange={(e) => setHeaderName(e.target.value)}
                  />
                )}
                <TextField
                  label="Secret"
                  size="small"
                  type="password"
                  value={secret}
                  onChange={(e) => setSecret(e.target.value)}
                  autoComplete="off"
                  helperText="Stored encrypted. Never shown again after saving."
                  slotProps={{ htmlInput: { "aria-label": "mcp-secret" } }}
                />
              </>
            )}

            {authMode === "per_user_static" && (
              <>
                <TextField
                  select
                  label="Secret type"
                  size="small"
                  value={secretMode}
                  onChange={(e) => setSecretMode(e.target.value as StaticSecretMode)}
                >
                  <MenuItem value="bearer">Bearer token</MenuItem>
                  <MenuItem value="header">Custom header</MenuItem>
                  <MenuItem value="basic">Basic auth</MenuItem>
                </TextField>
                {secretMode === "header" && (
                  <TextField
                    label="Header name"
                    size="small"
                    value={headerName}
                    onChange={(e) => setHeaderName(e.target.value)}
                  />
                )}
                <Alert severity="info" sx={{ mt: 1 }}>
                  No token pasted at install. Each end-user attaches their
                  own via POST&nbsp;
                  <code>/plugins/v1/connectors/&lt;slug&gt;/secret</code>&nbsp;
                  with their userJWT. Users without a token cannot use this
                  connector&apos;s tools.
                </Alert>
              </>
            )}

            {authMode === "client_credentials" && (
              <>
                <TextField
                  label="Client ID"
                  size="small"
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                  autoComplete="off"
                />
                <TextField
                  label="Client secret"
                  size="small"
                  type="password"
                  value={clientSecret}
                  onChange={(e) => setClientSecret(e.target.value)}
                  autoComplete="off"
                  helperText="Stored encrypted. Never shown again after saving."
                />
              </>
            )}

            {authMode === "custom_headers" && (
              <>
                <Typography variant="caption" color="text.secondary">
                  Add the request headers this server needs. Mark a row
                  &quot;Sensitive&quot; to encrypt its value at rest (e.g. an
                  Authorization header). Example: a Superset connection is an
                  <code> X-Superset-Url </code> header plus a sensitive
                  <code> Authorization: Basic … </code> header.
                </Typography>
                {headerRows.map((row, i) => (
                  <Stack key={i} direction="row" spacing={1} alignItems="center">
                    <TextField
                      label="Header name"
                      size="small"
                      value={row.name}
                      onChange={(e) =>
                        setHeaderRows((rows) =>
                          rows.map((r, j) =>
                            j === i ? { ...r, name: e.target.value } : r,
                          ),
                        )
                      }
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      label="Value"
                      size="small"
                      type={row.sensitive ? "password" : "text"}
                      value={row.value}
                      onChange={(e) =>
                        setHeaderRows((rows) =>
                          rows.map((r, j) =>
                            j === i ? { ...r, value: e.target.value } : r,
                          ),
                        )
                      }
                      autoComplete="off"
                      sx={{ flex: 1 }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={row.sensitive}
                          onChange={(e) =>
                            setHeaderRows((rows) =>
                              rows.map((r, j) =>
                                j === i
                                  ? { ...r, sensitive: e.target.checked }
                                  : r,
                              ),
                            )
                          }
                        />
                      }
                      label="Sensitive"
                    />
                    <Button
                      size="small"
                      color="inherit"
                      disabled={headerRows.length === 1}
                      onClick={() =>
                        setHeaderRows((rows) => rows.filter((_, j) => j !== i))
                      }
                      aria-label="remove-header"
                    >
                      <X size={16} />
                    </Button>
                  </Stack>
                ))}
                <Button
                  size="small"
                  startIcon={<Plus size={16} />}
                  onClick={() =>
                    setHeaderRows((rows) => [
                      ...rows,
                      { name: "", value: "", sensitive: false },
                    ])
                  }
                  sx={{ alignSelf: "flex-start" }}
                >
                  Add header
                </Button>
              </>
            )}

            {authMode === "user_oauth" && (
              <>
                <TextField
                  label="Authorization URL"
                  size="small"
                  value={authUrl}
                  onChange={(e) => setAuthUrl(e.target.value)}
                />
                <TextField
                  label="Token URL"
                  size="small"
                  value={tokenUrl}
                  onChange={(e) => setTokenUrl(e.target.value)}
                />
                <TextField
                  label="Client ID"
                  size="small"
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                />
                <TextField
                  label="Client secret"
                  size="small"
                  type="password"
                  value={clientSecret}
                  onChange={(e) => setClientSecret(e.target.value)}
                  autoComplete="off"
                />
                <TextField
                  label="Scopes"
                  size="small"
                  value={scopes}
                  onChange={(e) => setScopes(e.target.value)}
                  placeholder="space or comma separated"
                />
              </>
            )}
          </Stack>
        )}

        {step === "discover" && (
          <Box sx={{ mt: 1 }}>
            {needsAuth ? (
              // Static per-user connectors get a "paste your token" flow
              // instead of the OAuth Connect button. The distinction is the
              // active auth_mode: static (Shared credential wizard OR per-user
              // wizard both land here on needsAuth) → POST /secret; anything
              // else → OAuth /authorize.
              authMode === "static" || authMode === "per_user_static" ? (
                <Stack spacing={2}>
                  <Typography variant="body2" color="text.secondary">
                    Attach your token to discover this connector&apos;s tools.
                    Stored per-user, encrypted at rest.
                  </Typography>
                  <TextField
                    label="Your token"
                    size="small"
                    type="password"
                    value={secret}
                    onChange={(e) => setSecret(e.target.value)}
                    autoComplete="off"
                    helperText="Sent as Authorization: Bearer <token> (or the header you picked)."
                  />
                  <Button
                    variant="outlined"
                    disabled={busy || !secret.trim()}
                    onClick={handleAttachSecret}
                  >
                    Save token & retry
                  </Button>
                </Stack>
              ) : (
                <Stack spacing={2}>
                  <Typography variant="body2" color="text.secondary">
                    This connector needs you to authorize before its tools can be
                    discovered.
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<ExternalLink size={16} />}
                    // Enabled for any OAuth mode: handleConnect builds the
                    // authorize URL via POST /authorize on click (discover never
                    // returns it for user_oauth), so don't gate on a pre-fetched
                    // authorizeUrl — that left the button permanently disabled.
                    disabled={busy}
                    onClick={handleConnect}
                  >
                    Connect
                  </Button>
                  <Button
                    size="small"
                    startIcon={<RefreshCw size={16} />}
                    onClick={() => runDiscover()}
                    disabled={busy}
                  >
                    I&apos;ve connected — retry discovery
                  </Button>
                </Stack>
              )
            ) : tools.length === 0 ? (
              <Stack spacing={2} alignItems="flex-start">
                <Typography variant="body2" color="text.secondary">
                  No tools discovered yet.
                </Typography>
                <Button
                  size="small"
                  startIcon={<RefreshCw size={16} />}
                  onClick={() => runDiscover()}
                  disabled={busy}
                >
                  Retry discovery
                </Button>
              </Stack>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Discovered {tools.length} tool(s). Choose which to expose.
                </Typography>
                <Stack>
                  {tools.map((t) => (
                    <FormControlLabel
                      key={t.upstream_name}
                      control={
                        <Checkbox
                          checked={selected.has(t.upstream_name)}
                          onChange={() => toggle(t.upstream_name)}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2">{t.upstream_name}</Typography>
                          {t.description && (
                            <Typography variant="caption" color="text.secondary">
                              {t.description}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  ))}
                </Stack>
              </>
            )}
          </Box>
        )}

        {step === "done" && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2">
              <strong>{displayName || name}</strong> is connected with{" "}
              {selected.size} tool(s) enabled.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={close} disabled={busy}>
          {step === "done" ? "Close" : "Cancel"}
        </Button>

        {step === "connect" && (
          <Button
            variant="contained"
            onClick={handleAddAndDiscover}
            disabled={!canSubmitStep1 || busy}
            startIcon={busy ? <CircularProgress size={16} /> : undefined}
          >
            Add &amp; discover
          </Button>
        )}

        {step === "discover" && !needsAuth && tools.length > 0 && (
          <Button
            variant="contained"
            onClick={handleSaveSkills}
            disabled={busy}
            startIcon={busy ? <CircularProgress size={16} /> : undefined}
          >
            Save {selected.size} skill(s)
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
